import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Check, X, Send, ArrowLeft, MessageSquare, Users } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { btn, Empty } from "@/components/Page";
import { linkifyUrls } from "@/lib/textUtils";

// Format timestamp to exact date and time
function formatExactDateTime(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date)) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
}

function lastSeenText(iso) {
  if (!iso) return "offline";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (isNaN(mins) || mins < 0) return "offline";
  if (mins < 60) return `seen ${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `seen ${h}h ago`;
  const days = Math.floor(h / 24);
  return `seen ${days}d ago`;
}

export default function FriendChats({ data, reload }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);   // { user_id, name }
  const [convo, setConvo] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);

  const incoming = data?.incoming || [];
  const friends = data?.friends || [];

  useEffect(() => {
    const el = scrollRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [convo]);

  const onScroll = (e) => {
    const el = e.target;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const openConvo = async (f) => {
    setSelected({ user_id: f.user_id, name: f.name, online: f.online, last_seen: f.last_seen });
    setConvo([]);
    atBottomRef.current = true;
    try {
      const { data: res } = await api.get(`/friends/${f.user_id}/chat`);
      setConvo(res.messages || []);
      reload && reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const send = async () => {
    if (!text.trim() || !selected) return;
    setBusy(true);
    try {
      const { data: msg } = await api.post(`/friends/${selected.user_id}/chat`, { body: text.trim() });
      atBottomRef.current = true;
      setConvo((c) => [...c, msg]);
      setText("");
      reload && reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const respond = async (fid, action) => {
    try {
      await api.post(`/friends/${fid}/${action}`);
      toast.success(action === "accept" ? "Friend added" : "Request declined");
      reload && reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="friend-chats">
      {incoming.length > 0 && (
        <div className="mb-5" data-testid="friend-requests">
          <h3 className="text-[11px] uppercase tracking-widest text-amber-300 font-cinzel mb-2">Friend Requests ({incoming.length})</h3>
          <div className="space-y-2">
            {incoming.map((r) => (
              <div key={r.friendship_id} data-testid={`friend-request-${r.user_id}`}
                className="flex items-center justify-between gap-2 px-4 py-2.5 border border-amber-700/40 bg-amber-900/10">
                <span className="flex items-center gap-2 text-zinc-100 min-w-0"><UserPlus size={15} className="text-amber-400 shrink-0" /><span className="truncate">{r.name}</span></span>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`accept-request-${r.user_id}`} onClick={() => respond(r.friendship_id, "accept")}
                    className="text-xs px-3 py-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1"><Check size={13} /> Accept</button>
                  <button data-testid={`decline-request-${r.user_id}`} onClick={() => respond(r.friendship_id, "decline")}
                    className="text-xs px-3 py-1.5 rounded-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center gap-1"><X size={13} /> Decline</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {friends.length === 0 && incoming.length === 0 ? (
        <Empty label="No friends yet. Visit a lanista's profile and tap “Add Friend” to start a conversation here." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Friends list */}
          <div className={`md:col-span-1 ${selected ? "hidden md:block" : "block"}`}>
            <h3 className="text-[11px] uppercase tracking-widest text-zinc-400 font-cinzel mb-2 flex items-center gap-1.5"><Users size={13} /> Friends ({friends.length})</h3>
            <div className="space-y-1.5" data-testid="friends-list">
              {friends.map((f) => (
                <button key={f.user_id} data-testid={`friend-${f.user_id}`} onClick={() => openConvo(f)}
                  className={`w-full text-left px-3 py-2.5 border transition-colors ${
                    selected?.user_id === f.user_id ? "border-amber-600 bg-amber-900/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-manrope text-zinc-100 truncate flex items-center gap-2">
                      <span data-testid={`friend-online-${f.user_id}`} title={f.online ? "Online" : lastSeenText(f.last_seen)}
                        className={`w-2 h-2 rounded-full shrink-0 ${f.online ? "bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.8)]" : "bg-zinc-600"}`} />
                      <span className="truncate">{f.name}</span>
                    </span>
                    {f.unread > 0 && <span data-testid={`friend-unread-${f.user_id}`} className="min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-amber-500 text-zinc-950 text-[10px] font-bold">{f.unread}</span>}
                  </div>
                  {f.last_body
                    ? <p className="text-xs text-zinc-500 truncate mt-0.5">{f.last_body}</p>
                    : <p className="text-xs text-zinc-600 truncate mt-0.5">{f.online ? "Online" : lastSeenText(f.last_seen)}</p>}
                </button>
              ))}
              {friends.length === 0 && <p className="text-xs text-zinc-600">No friends yet — accept a request above.</p>}
            </div>
          </div>

          {/* Conversation */}
          <div className={`md:col-span-2 ${selected ? "block" : "hidden md:block"}`}>
            {!selected ? (
              <div className="h-full min-h-[260px] flex items-center justify-center border border-zinc-800/70 text-zinc-600 text-sm">
                <span className="flex items-center gap-2"><MessageSquare size={16} /> Select a friend to view your conversation</span>
              </div>
            ) : (
              <div className="border border-zinc-800 flex flex-col h-[60vh] md:h-[460px]" data-testid="friend-conversation">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800 bg-zinc-950">
                  <button className="md:hidden text-zinc-400" onClick={() => setSelected(null)} data-testid="convo-back-btn"><ArrowLeft size={18} /></button>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${selected.online ? "bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.8)]" : "bg-zinc-600"}`} />
                  <button data-testid="convo-friend-name" onClick={() => navigate(`/profile/${selected.user_id}`)}
                    title={`View ${selected.name}'s profile`}
                    className="font-cinzel text-amber-200 tracking-wide hover:text-amber-100 hover:underline underline-offset-2 cursor-pointer transition-colors">{selected.name}</button>
                  <span className="text-[11px] text-zinc-500">{selected.online ? "online" : lastSeenText(selected.last_seen)}</span>
                </div>
                <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
                  {convo.length === 0 ? (
                    <p className="text-center text-zinc-600 text-sm mt-8">No messages yet. Say hello!</p>
                  ) : convo.map((m) => {
                    const mine = m.from_user === selected.user_id ? false : true;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-amber-900/30 border border-amber-800/40 text-amber-50" : "bg-zinc-900 border border-zinc-800 text-zinc-200"}`}>
                          <p className="whitespace-pre-line break-words">{linkifyUrls(m.body, `msg-${m.id}`)}</p>
                          <span className="block text-[10px] text-zinc-500 mt-1 text-right">{formatExactDateTime(m.created_at)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form className="flex gap-2 p-3 border-t border-zinc-800" onSubmit={(e) => { e.preventDefault(); send(); }}>
                  <input data-testid="friend-chat-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
                    placeholder={`Message ${selected.name}…`}
                    className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none text-sm" />
                  <button data-testid="friend-chat-send" type="submit" disabled={busy || !text.trim()} className={btn() + " px-4"}><Send size={15} /></button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
